import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger } from "@/components/ui/select";
import { ExternalLink, FlaskConical, ChevronDown, ChevronUp, GitCompareArrows, Search, X, ShieldCheck, Info, BookmarkPlus } from "lucide-react";

const QUALITY_BG = {
  High: "bg-emerald-50 text-emerald-700 border-emerald-200",
  Moderate: "bg-indigo-50 text-indigo-700 border-indigo-200",
  Low: "bg-amber-50 text-amber-700 border-amber-200",
  "Not yet assessed": "bg-slate-50 text-slate-500 border-slate-200",
};
const VERIF_BG = {
  "Expert validated": "bg-emerald-50 text-emerald-700 border-emerald-200",
  "Full source reviewed": "bg-teal-50 text-teal-700 border-teal-200",
  "Citation verified": "bg-sky-50 text-sky-700 border-sky-200",
  "Verification pending": "bg-amber-50 text-amber-700 border-amber-200",
  Excluded: "bg-red-50 text-red-700 border-red-200",
};
const REL_BG = {
  Direct: "bg-indigo-50 text-indigo-700 border-indigo-100",
  Indirect: "bg-slate-50 text-slate-600 border-slate-200",
  Contextual: "bg-slate-50 text-slate-500 border-slate-200",
};
const TONE_STYLE = {
  aligned: "border-l-emerald-500",
  partial: "border-l-teal-500",
  exceeds: "border-l-amber-500",
  untranslated: "border-l-indigo-500",
  unstudied: "border-l-purple-500",
  conflict: "border-l-[#E06C6C]",
  insufficient: "border-l-slate-400",
};

const FILTERS = {
  source_type: ["Peer-reviewed publication", "Congress abstract"],
  evidence_type: ["Randomized controlled trial", "Subgroup analysis", "Real-world evidence", "Patient-reported outcomes"],
  geography: ["Global", "North America", "Europe"],
  theme_id: [["t1", "Sequencing & Selection"], ["t2", "ESR1 Testing"], ["t3", "Competitive SERDs"], ["t4", "Combinations"], ["t5", "Efficacy Durability"], ["t6", "Tolerability"]],
  competitor: ["None", "Camizestrant", "Imlunestrant", "Vepdegestrant"],
  verification: ["Expert validated", "Full source reviewed", "Citation verified", "Verification pending"],
  quality: ["High", "Moderate", "Low", "Not yet assessed"],
  relevance: ["Direct", "Indirect", "Contextual"],
};
const LABELS = { source_type: "Publication type", evidence_type: "Evidence type", geography: "Geography", theme_id: "Clinical theme", competitor: "Asset / competitor", verification: "Verification", quality: "Quality", relevance: "Relevance" };
const SORTS = { relevance: "Relevance", date: "Publication date", quality: "Evidence quality", alpha: "Alphabetical" };

const Field = ({ label, value }) => (
  <div>
    <p className="text-[9px] font-mono uppercase tracking-wide text-slate-400">{label}</p>
    <p className="text-xs text-slate-700 leading-snug">{value || "Not reported"}</p>
  </div>
);

const SectionLabel = ({ children }) => (
  <p className="font-mono text-[10px] uppercase tracking-widest text-teal-700 mb-2">{children}</p>
);

export default function EvidenceLandscape({ globalFilters = {}, onOpenVoice }) {
  const [items, setItems] = useState([]);
  const [voiceMap, setVoiceMap] = useState([]);
  const [filters, setFilters] = useState({});
  const [search, setSearch] = useState("");
  const [dateFrom, setDateFrom] = useState("all");
  const [sortBy, setSortBy] = useState("relevance");
  const [open, setOpen] = useState(null);
  const [whyQuality, setWhyQuality] = useState(null);
  const [openCat, setOpenCat] = useState(null);

  useEffect(() => { api.get("/evidence").then((r) => setItems(r.data.evidence)); }, []);
  useEffect(() => { api.get("/evidence/voice-map").then((r) => setVoiceMap(r.data.map)); }, []);
  useEffect(() => {
    const saved = localStorage.getItem("evidence_saved_view");
    if (saved) { const v = JSON.parse(saved); setFilters(v.filters || {}); setDateFrom(v.dateFrom || "all"); setSortBy(v.sortBy || "relevance"); }
  }, []);

  const REL_RANK = { Direct: 0, Indirect: 1, Contextual: 2 };
  const Q_RANK = { High: 0, Moderate: 1, Low: 2, "Not yet assessed": 3 };
  const gf = globalFilters || {};
  const gActive = (k) => gf[k] && gf[k] !== "all";

  const visible = useMemo(() => {
    let list = items.filter((e) => {
      if (search && !(`${e.title} ${e.citation}`.toLowerCase().includes(search.toLowerCase()))) return false;
      for (const k of ["source_type", "evidence_type", "geography", "theme_id", "competitor"]) {
        if (filters[k] && filters[k] !== "all" && e[k] !== filters[k]) return false;
      }
      if (filters.verification && filters.verification !== "all" && e.verification_status !== filters.verification) return false;
      if (filters.quality && filters.quality !== "all" && e.quality !== filters.quality) return false;
      if (filters.relevance && filters.relevance !== "all" && e.relevance !== filters.relevance) return false;
      if (dateFrom !== "all" && e.date < dateFrom) return false;
      if (gActive("evidence_type") && e.evidence_type !== gf.evidence_type) return false;
      if (gActive("theme_id") && e.theme_id !== gf.theme_id) return false;
      if (gActive("competitor") && !(e.competitor || "").toLowerCase().includes(gf.competitor.split(" ")[0].toLowerCase())) return false;
      if (gf.date_from && e.date < gf.date_from) return false;
      if (gf.date_to && e.date > gf.date_to) return false;
      return true;
    });
    const sorters = {
      relevance: (a, b) => (REL_RANK[a.relevance] ?? 3) - (REL_RANK[b.relevance] ?? 3),
      date: (a, b) => b.date.localeCompare(a.date),
      quality: (a, b) => (Q_RANK[a.quality] ?? 4) - (Q_RANK[b.quality] ?? 4),
      alpha: (a, b) => a.title.localeCompare(b.title),
    };
    return [...list].sort(sorters[sortBy]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items, filters, search, dateFrom, sortBy, globalFilters]);

  const chips = [
    ...Object.entries(filters).filter(([, v]) => v && v !== "all").map(([k, v]) => [k, LABELS[k], k === "theme_id" ? FILTERS.theme_id.find(([id]) => id === v)?.[1] : v]),
    ...(dateFrom !== "all" ? [["__date", "Date", `Since ${dateFrom.slice(0, 4)}`]] : []),
  ];

  const overview = useMemo(() => {
    const verified = items.filter((e) => ["Expert validated", "Full source reviewed"].includes(e.verification_status)).length;
    const families = new Set(items.map((e) => e.evidence_family)).size;
    const dates = items.map((e) => e.date).sort();
    return {
      total: items.length, verified, pending: items.length - verified,
      types: new Set(items.map((e) => e.evidence_type)).size,
      geos: [...new Set(items.map((e) => e.geography))],
      range: dates.length ? `${dates[0].slice(0, 4)}–${dates[dates.length - 1].slice(0, 4)}` : "—",
      themes: new Set(items.map((e) => e.theme_id)).size, families,
    };
  }, [items]);

  const approved = visible.filter((e) => e.approved_for_analysis);
  const others = visible.filter((e) => !e.approved_for_analysis);
  const evTitle = (id) => items.find((x) => x.id === id)?.title || id;

  const renderCard = (e) => (
    <div key={e.id} data-testid={`evidence-item-${e.id}`} className="bg-white border border-slate-200/80 rounded-xl shadow-sm hover:border-teal-400 transition-colors">
      <button className="w-full text-left p-5" onClick={() => setOpen(open === e.id ? null : e.id)} data-testid={`evidence-toggle-${e.id}`}>
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <h4 className="text-sm font-bold text-slate-900 leading-snug flex items-center gap-2"><FlaskConical className="w-3.5 h-3.5 text-teal-600 shrink-0" />{e.title}</h4>
            <p className="text-xs text-slate-500 mt-0.5">{e.citation} · {e.date} · {e.evidence_type}{e.competitor !== "None" && <span className="text-slate-600 font-medium"> · {e.competitor}</span>}</p>
          </div>
          {open === e.id ? <ChevronUp className="w-4 h-4 text-slate-400 shrink-0" /> : <ChevronDown className="w-4 h-4 text-slate-400 shrink-0" />}
        </div>
        <div className="flex flex-wrap items-center gap-1.5 mt-2">
          <Badge variant="outline" className={`text-[10px] ${VERIF_BG[e.verification_status]}`} data-testid={`verification-${e.id}`}>{e.verification_status}</Badge>
          <Badge variant="outline" className={`text-[10px] ${QUALITY_BG[e.quality]}`}>{e.quality === "Not yet assessed" ? "Quality not yet assessed" : `${e.quality} quality`}</Badge>
          <Badge variant="outline" className={`text-[10px] ${REL_BG[e.relevance]}`}>Relevance: {e.relevance}</Badge>
        </div>
        <p className="text-xs text-slate-600 mt-2 leading-snug">{e.finding_neutral}</p>
        <div className="flex flex-wrap gap-x-3 gap-y-1 mt-2 text-[10.5px]">
          {e.supports.length > 0 && (
            <span role="link" tabIndex={0} onClick={(ev) => { ev.stopPropagation(); onOpenVoice?.(); }}
              className="text-emerald-700 font-semibold hover:underline cursor-pointer" data-testid={`supports-link-${e.id}`}>
              Supports {e.supports.length} HCP insight{e.supports.length > 1 ? "s" : ""}</span>
          )}
          {e.contradicts.length > 0 && (
            <span role="link" tabIndex={0} onClick={(ev) => { ev.stopPropagation(); onOpenVoice?.(); }}
              className="text-[#B34A4A] font-semibold hover:underline cursor-pointer">
              Contradicts {e.contradicts.length} HCP insight{e.contradicts.length > 1 ? "s" : ""}</span>
          )}
          <span className="text-slate-400">Evidence family: <span className="text-slate-600 font-medium">{e.evidence_family}</span></span>
        </div>
      </button>
      {open === e.id && (
        <div className="px-5 pb-5 space-y-3" data-testid={`evidence-detail-${e.id}`}>
          <div className="bg-slate-50 border border-slate-200/80 rounded-lg p-4">
            <SectionLabel>Source information</SectionLabel>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-x-4 gap-y-2.5">
              <Field label="Full citation" value={e.citation} />
              <Field label="Identifier" value={e.url?.includes("doi") ? `DOI: ${e.url.split("doi.org/")[1]}` : "See source link"} />
              <Field label="Source type" value={e.source_type} />
              <Field label="Publication date" value={e.date} />
              <Field label="Access classification" value={`${e.access_class} · ${e.access_note}`} />
              <Field label="Verification status" value={e.verification_status} />
              <Field label="Evidence origin" value={e.evidence_origin} />
              <Field label="Applicability" value={e.applicability} />
            </div>
            <p className="text-[10.5px] text-slate-500 mt-2 leading-snug"><span className="font-mono text-[9px] uppercase tracking-wide text-slate-400 mr-1">Applicability note</span>{e.applicability_note}</p>
          </div>
          <div className="bg-slate-50 border border-slate-200/80 rounded-lg p-4">
            <SectionLabel>Study information</SectionLabel>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-x-4 gap-y-2.5">
              <Field label="Study design" value={e.study_design} />
              <Field label="Population" value={e.population} />
              <Field label="Intervention" value={e.intervention} />
              <Field label="Comparator" value={e.comparator} />
              <Field label="Outcomes" value={e.outcomes} />
              <Field label="Study data cut-off" value={e.evidence_cutoff || "Not reported"} />
            </div>
          </div>
          <div className="bg-slate-50 border border-slate-200/80 rounded-lg p-4">
            <SectionLabel>Findings</SectionLabel>
            <p className="text-xs text-slate-700 leading-snug"><span className="font-mono text-[9px] uppercase tracking-wide text-slate-400 mr-1.5">Reported study result</span>{e.key_findings}</p>
            <p className="text-[10.5px] text-slate-500 mt-1.5">Reported values are illustrative until source verification is complete. Subgroup findings must not be generalised to the full study population.</p>
          </div>
          <div className="bg-slate-50 border border-slate-200/80 rounded-lg p-4">
            <SectionLabel>Critical appraisal</SectionLabel>
            <div className="flex flex-wrap items-center gap-1.5 mb-2">
              <Badge variant="outline" className={`text-[10px] ${QUALITY_BG[e.quality]}`}>{e.quality === "Not yet assessed" ? "Quality not yet assessed" : `${e.quality} quality`}</Badge>
              <Badge variant="outline" className={`text-[10px] ${REL_BG[e.relevance]}`}>Relevance: {e.relevance}</Badge>
              <button onClick={() => setWhyQuality(whyQuality === e.id ? null : e.id)} data-testid={`why-quality-${e.id}`}
                className="text-[10px] font-semibold text-teal-700 border border-teal-200 bg-teal-50 rounded-full px-2.5 py-0.5 hover:bg-teal-100">Why this quality?</button>
            </div>
            {whyQuality === e.id && <p className="text-[11px] text-slate-600 bg-white border border-slate-200 rounded-lg px-3 py-2 mb-2" data-testid={`quality-rationale-${e.id}`}>{e.quality_rationale}</p>}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <p className="font-mono text-[9px] uppercase tracking-wide text-amber-700 mb-0.5">Important limitations</p>
                <p className="text-xs text-slate-700">{e.limitations}</p>
              </div>
              <div>
                <p className="font-mono text-[9px] uppercase tracking-wide text-indigo-700 mb-0.5">MarksMan reviewer interpretation</p>
                <p className="text-xs text-slate-700">{e.reviewer_comment}</p>
              </div>
            </div>
            <p className="text-[10px] text-slate-400 mt-2">Scientific-review status: {e.approved_for_analysis ? "Approved for analysis" : "In review — not yet contributing to approved outputs"}</p>
          </div>
          <div className="bg-slate-50 border border-slate-200/80 rounded-lg p-4">
            <SectionLabel>Evidence-to-Voice connections</SectionLabel>
            <div className="flex flex-wrap gap-2 text-[11px]">
              {e.supports.length > 0 && <button onClick={() => onOpenVoice?.()} className="text-emerald-700 font-semibold bg-emerald-50 border border-emerald-200 rounded-full px-3 py-1 hover:bg-emerald-100">View {e.supports.length} supported HCP statement{e.supports.length > 1 ? "s" : ""} →</button>}
              {e.contradicts.length > 0 && <button onClick={() => onOpenVoice?.()} className="text-[#B34A4A] font-semibold bg-red-50/70 border border-red-200 rounded-full px-3 py-1 hover:bg-red-100">View {e.contradicts.length} contradicted statement{e.contradicts.length > 1 ? "s" : ""} →</button>}
              <span className="text-slate-500 px-1 py-1">Linked theme: {FILTERS.theme_id.find(([id]) => id === e.theme_id)?.[1]}</span>
            </div>
          </div>
          <a href={e.url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-xs font-semibold text-indigo-700 hover:text-indigo-900">
            <ExternalLink className="w-3 h-3" />Source link <span className="text-slate-400 font-normal">({e.access_note.toLowerCase()} — access to restricted full text is not implied)</span>
          </a>
        </div>
      )}
    </div>
  );

  return (
    <div className="space-y-5" data-testid="evidence-view">
      <div className="sticky top-0 z-20 bg-white border border-slate-200/80 rounded-xl p-3 shadow-sm space-y-2" data-testid="evidence-filters">
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative w-56">
            <Search className="w-3.5 h-3.5 text-slate-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
            <Input data-testid="evidence-search" placeholder="Search title, author, trial…" value={search} onChange={(e) => setSearch(e.target.value)}
              className="h-8 pl-8 text-xs rounded-full bg-slate-50 border-slate-200" />
          </div>
          {Object.keys(LABELS).map((key) => (
            <Select key={key} value={filters[key] || "all"} onValueChange={(v) => setFilters({ ...filters, [key]: v })}>
              <SelectTrigger data-testid={`evidence-filter-${key}`} className="w-auto h-8 text-xs gap-1.5 rounded-full border-slate-200 bg-slate-50">
                <span className="text-slate-400">{LABELS[key]}:</span>
                {filters[key] && filters[key] !== "all" ? (key === "theme_id" ? FILTERS.theme_id.find(([id]) => id === filters[key])?.[1] : filters[key]) : "All"}
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                {FILTERS[key].map((o) => Array.isArray(o)
                  ? <SelectItem key={o[0]} value={o[0]}>{o[1]}</SelectItem>
                  : <SelectItem key={o} value={o}>{o}</SelectItem>)}
              </SelectContent>
            </Select>
          ))}
          <Select value={dateFrom} onValueChange={setDateFrom}>
            <SelectTrigger data-testid="evidence-filter-date" className="w-auto h-8 text-xs gap-1.5 rounded-full border-slate-200 bg-slate-50">
              <span className="text-slate-400">Date:</span>{dateFrom === "all" ? "All" : `Since ${dateFrom.slice(0, 4)}`}
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              <SelectItem value="2024-01-01">Since 2024</SelectItem>
              <SelectItem value="2025-01-01">Since 2025</SelectItem>
              <SelectItem value="2026-01-01">Since 2026</SelectItem>
            </SelectContent>
          </Select>
          <Select value={sortBy} onValueChange={setSortBy}>
            <SelectTrigger data-testid="evidence-sort" className="w-auto h-8 text-xs gap-1.5 rounded-full border-slate-200 bg-white">
              <span className="text-slate-400">Sort:</span>{SORTS[sortBy]}
            </SelectTrigger>
            <SelectContent>
              {Object.entries(SORTS).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}
            </SelectContent>
          </Select>
          <button data-testid="evidence-save-view" title="Save current filters as default view"
            onClick={() => localStorage.setItem("evidence_saved_view", JSON.stringify({ filters, dateFrom, sortBy }))}
            className="flex items-center gap-1 h-8 px-3 text-xs font-medium text-slate-600 border border-slate-200 rounded-full hover:border-teal-400 hover:text-teal-700">
            <BookmarkPlus className="w-3.5 h-3.5" />Save view</button>
        </div>
        {chips.length > 0 && (
          <div className="flex flex-wrap items-center gap-1.5" data-testid="evidence-filter-chips">
            {chips.map(([k, l, v]) => (
              <button key={k} onClick={() => (k === "__date" ? setDateFrom("all") : setFilters({ ...filters, [k]: "all" }))}
                className="flex items-center gap-1 text-[10.5px] font-medium text-teal-800 bg-teal-50 border border-teal-200 rounded-full px-2.5 py-0.5 hover:bg-teal-100">
                {l}: {v} <X className="w-3 h-3" /></button>
            ))}
            <button data-testid="evidence-clear-all" onClick={() => { setFilters({}); setDateFrom("all"); setSearch(""); }}
              className="text-[10.5px] font-semibold text-red-600 hover:text-red-800 ml-1">Clear all</button>
          </div>
        )}
      </div>

      <div className="bg-white border border-slate-200/80 rounded-xl p-4 shadow-sm" data-testid="evidence-overview">
        <div className="flex flex-wrap gap-x-6 gap-y-2 items-center">
          {[["Evidence items", overview.total, null],
            ["Verified sources", overview.verified, () => setFilters({ ...filters, verification: "Expert validated" })],
            ["Pending verification", overview.pending, () => setFilters({ ...filters, verification: "Verification pending" })],
            ["Evidence types", overview.types, null], ["Clinical themes", overview.themes, null], ["Date range", overview.range, null],
            ["Evidence families", `${overview.total} records · ${overview.families} independent families`, null]].map(([l, v, onClick]) => (
            <button key={l} onClick={onClick || undefined} disabled={!onClick}
              className={`text-left ${onClick ? "hover:bg-teal-50/60 rounded-md px-1 -mx-1 cursor-pointer" : "cursor-default"}`}>
              <p className="font-mono text-[8.5px] uppercase tracking-widest text-slate-400">{l}</p>
              <p className="text-xs font-bold text-slate-800">{v}</p>
            </button>
          ))}
        </div>
        <p className="text-[10.5px] text-slate-500 mt-2 flex items-center gap-1.5"><Info className="w-3 h-3 text-amber-600" />Key evidence gaps: prospective combination data (ELEVATE), post-elacestrant sequencing, community real-world outcomes. Related records are grouped by evidence family and are not counted as independent corroboration.</p>
      </div>

      <div className="space-y-3">
        <p className="text-xs text-slate-500 font-mono uppercase tracking-widest" data-testid="evidence-count">
          {visible.length} evidence items{(["evidence_type", "theme_id", "competitor"].some(gActive) || gf.date_from || gf.date_to) && <span className="text-teal-600"> · global filters applied</span>}
        </p>
        {approved.length > 0 && (
          <>
            <p className="text-[10.5px] font-semibold text-emerald-700 flex items-center gap-1.5"><ShieldCheck className="w-3.5 h-3.5" />Approved for analysis — contributes to executive intelligence and reports</p>
            {approved.map(renderCard)}
          </>
        )}
        {others.length > 0 && (
          <>
            <p className="text-[10.5px] font-semibold text-slate-500 flex items-center gap-1.5 pt-2"><Info className="w-3.5 h-3.5" />In verification / review — shown for context, excluded from approved outputs</p>
            {others.map(renderCard)}
          </>
        )}
      </div>

      <div data-testid="evidence-voice-map">
        <div className="flex items-center gap-2 mb-1">
          <GitCompareArrows className="w-4 h-4 text-teal-600" />
          <h3 className="font-heading text-lg font-bold text-slate-900">Evidence-to-Voice Map</h3>
        </div>
        <p className="text-xs text-slate-500 mb-3">Where HCP perception and the evidence base align, diverge, or leave each other behind. Expand one category at a time.</p>
        <div className="space-y-2">
          {voiceMap.map((cat) => {
            const isOpen = openCat === cat.tone;
            return (
              <div key={cat.category} className={`bg-white border border-slate-200/80 border-l-4 rounded-xl shadow-sm ${TONE_STYLE[cat.tone]}`} data-testid={`voice-map-${cat.tone}`}>
                <button className="w-full text-left px-5 py-3.5 flex items-center gap-3" onClick={() => setOpenCat(isOpen ? null : cat.tone)} data-testid={`voice-map-toggle-${cat.tone}`}>
                  <div className="min-w-0 flex-1">
                    <h4 className="text-sm font-bold text-slate-900">{cat.category} <span className="font-mono text-xs font-normal text-slate-400 ml-1">{cat.items.length}</span></h4>
                    {!isOpen && <p className="text-[11px] text-slate-500 truncate mt-0.5">Top finding: {cat.items[0]?.text}</p>}
                  </div>
                  <Badge variant="outline" className="text-[9px] bg-slate-50 text-slate-500 border-slate-200 shrink-0 hidden sm:inline-flex">Mapping confidence: {cat.items[0]?.mapping_confidence}</Badge>
                  {isOpen ? <ChevronUp className="w-4 h-4 text-slate-400 shrink-0" /> : <ChevronDown className="w-4 h-4 text-slate-400 shrink-0" />}
                </button>
                {isOpen && (
                  <div className="px-5 pb-4 space-y-3">
                    {cat.items.map((it) => (
                      <div key={it.text} className="bg-slate-50 border border-slate-200/80 rounded-lg p-3.5">
                        <p className="text-xs text-slate-800 font-medium leading-snug">{it.text}</p>
                        <div className="flex flex-wrap items-center gap-1.5 mt-2">
                          <Badge variant="outline" className="text-[9px] bg-slate-50 text-slate-500 border-slate-200">Mapping confidence: {it.mapping_confidence}</Badge>
                          <Badge variant="outline" className="text-[9px] bg-slate-50 text-slate-500 border-slate-200">{it.geography}</Badge>
                          <Badge variant="outline" className="text-[9px] bg-amber-50 text-amber-700 border-amber-200">{it.review_status}</Badge>
                        </div>
                        <p className="text-[11px] text-slate-600 mt-2"><span className="font-mono text-[9px] uppercase tracking-wide text-teal-700 mr-1.5">Strategic implication</span>{it.implication}</p>
                        <div className="flex flex-wrap gap-x-3 gap-y-1 mt-2 text-[10.5px]">
                          {it.evidence_ids.length > 0 && (
                            <span className="text-indigo-700 font-medium">{it.evidence_ids.length} scientific source{it.evidence_ids.length > 1 ? "s" : ""}: {it.evidence_ids.map(evTitle).map((t) => t.split(":")[0]).join(" · ")}</span>
                          )}
                          {it.insight_ids.length > 0 && (
                            <button onClick={() => onOpenVoice?.()} className="text-teal-700 font-semibold hover:underline">View {it.insight_ids.length} supporting HCP statement{it.insight_ids.length > 1 ? "s" : ""} →</button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
